<template>
<div class="sp-content-wrapper">
    <div class="sp-content-header">
        <h1>Products</h1>
    </div>
    <div class="sp-content">
        <div class="sp-notice">
            The data is current as of the scan date of each component. For Ubuntu, the last scan date is always at the moment of release.
        </div>
        <div class="sp-filterbox-wrapper">
            <div class="sp-filterbox">
                <div class="sp-filters">
                    <v-select
                        v-model="base"
                        :options="product_list"
                        label="nameversion"
                        placeholder="Select base release"
                    />
                    <v-select
                        v-model="target"
                        :options="product_list"
                        label="nameversion"
                        placeholder="Select target release"
                    />
                    <v-select
                        v-model="component_filter"
                        :options="compareData.component_names"
                        placeholder="All components"
                        :multiple="true"
                    />
                </div>
            </div>
        </div>

        <div v-if="base === null || target === null" class="sp-empty-state">
            Select a product for both Base and Target to compare.
        </div>

        <div v-else>
            <div class="cvetable-row cvetable-row-header">
                <div class="cvetable-col save-csv clickable" title="Save as CSV" @click="saveComponentsToCSV">
                    <span class="material-symbols-outlined">save</span>
                </div>
                <div class="cvetable-col col-2">Components (base &#8594; target)</div>
            </div>
            <div class="sp-section">
                <div class="sp-product-card-wrapper">
                    <table class="sp-product-header-table">
                        <thead>
                            <tr>
                                <th scope="col">Component</th>
                                <th scope="col">Base scan date</th>
                                <th scope="col">Target scan date</th>
                                <th v-for="severity in compareData.severities" :key="severity.id" scope="col">{{ severity.name }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="component in compareData.components" :key="component.name">
                                <td>{{ component.name }}_{{ component.base_versions }} &#8594; {{ component.name }}_{{ component.target_versions }}</td>
                                <td>{{ component.base_scan_date || 'never scanned' }}</td>
                                <td>{{ component.target_scan_date || 'never scanned' }}</td>
                                <td v-for="severity in component.severities" :key="severity.id">
                                    {{ severity.base_count }} &#8594; {{ severity.target_count }}
                                </td>
                            </tr>
                            <tr v-if="compareData.components.length === 0">
                                <td :colspan="3 + compareData.severities.length">No matching components between the selected releases.</td>
                            </tr>
                            <tr v-if="compareData.components.length > 0" style="font-weight:bold">
                                <td>Total</td>
                                <td />
                                <td />
                                <td v-for="severity in compareData.severities" :key="severity.id">
                                    {{ totalCount(severity.id, 'base_count') }} &#8594; {{ totalCount(severity.id, 'target_count') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="cvetable-row cvetable-row-header">
                <div class="cvetable-col save-csv clickable" title="Save as CSV" @click="saveClosedCvesToCSV">
                    <span class="material-symbols-outlined">save</span>
                </div>
                <div class="cvetable-col col-2">CVEs closed in target</div>
            </div>
            <div class="sp-section">
                <div class="sp-product-card-wrapper">
                    <table class="sp-product-header-table">
                        <thead>
                            <tr>
                                <th scope="col">CVE</th>
                                <th scope="col">Component</th>
                                <th scope="col">Mirantis severity</th>
                                <th scope="col">Package</th>
                                <th scope="col">Fix version</th>
                                <th scope="col">Artifact</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(cve, index) in compareData.closed_cves" :key="cve.name + cve.component + cve.artifact_id + index">
                                <td>{{ cve.name }}</td>
                                <td>{{ cve.component }}</td>
                                <td>{{ cve.severity }}</td>
                                <td>{{ cve.package_name }} ({{ cve.package_version }})</td>
                                <td>{{ cve.fix_version }}</td>
                                <td>{{ cve.image }}</td>
                            </tr>
                            <tr v-if="compareData.closed_cves.length === 0">
                                <td colspan="6">No CVEs closed between the selected releases.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<style scoped>
.sp-notice {
    margin: 0 13px 10px 13px;
    padding: 8px 12px;
    color: var(--color-severity-high);
    font-weight: 700;
    background-color: var(--color-bg-header);
    border-left: 4px solid var(--color-severity-high);
}
.sp-empty-state {
    padding: 15px;
    color: var(--color-fg);
    opacity: 0.7;
}
.sp-section {
    display: flex;
    flex-wrap: wrap;
}
.sp-product-card-wrapper {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    padding-left: 13px;
    width: 100%;
}
.sp-product-header-table {
    margin-bottom: 10px;
    justify-content: space-between;
    align-items: left;
    background-color: var(--color-bg-surface);
    border-top: 1px solid var(--color-border-header);
    border-bottom: 0.1px solid var(--color-border-header);
    margin-bottom: 10px;
}
.sp-product-header-table th {
    padding: 3px;
    text-align: left;
    border-bottom: 0.1px solid var(--color-border-header);
}
.sp-product-header-table td {
    padding: 2px;
    text-align: left;
    border-bottom: 0.1px solid var(--color-border-header);
}
.sp-product-header-table tr:hover {
    background-color: var(--color-bg-header);
}
.sp-product-header-table tbody tr:last-child td {
    border-bottom: 0;
}
.cvetable-row {
    line-height: 50px;
    padding-left: 8px;
    padding-right: 8px;
    margin-bottom: 5px;
}
.cvetable-row-header {
    display: flex;
    justify-content: flex-start;
    background-color: var(--color-bg-header);
    border-top: 2px solid var(--color-border-header);
    border-bottom: 1px solid var(--color-border-header);
    line-height: 40px;
    font-weight: 600;
    padding-left: 13px;
}
.col-2 {
    width: 16%;
}
.save-csv {
    display: flex;
    align-items: center;
    margin-right: 8px;
}
a:link {
    color: var(--color-fg);
    background-color: transparent;
    text-decoration: underline;
}
a:visited {
    color: var(--color-fg);
    background-color: transparent;
    text-decoration: none;
}
</style>

<script>
import vSelect from 'vue-select'
import { mapActions } from 'vuex';

export default {
    name: 'ProductsView',
    data() {
        return {
            product_list: [],
            base: null,
            target: null,
            component_filter: [],
            compareData: { component_names: [], severities: [], components: [], closed_cves: [] },
        }
    },
    components: {
        vSelect,
    },
    methods: {
        ...mapActions(['toggleLoaderSpinner']),
        showLoader() {
            this.toggleLoaderSpinner(true);
        },
        hideLoader() {
            this.toggleLoaderSpinner(false);
        },
        totalCount(severityId, key) {
            return this.compareData.components.reduce((sum, component) => {
                const severity = component.severities.find((el) => el.id == severityId)
                return sum + (severity ? severity[key] : 0)
            }, 0)
        },
        csvCell(value) {
            const text = (value === null || value === undefined) ? '' : String(value)
            return /[",\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text
        },
        downloadCSV(rows, filename) {
            const csvData = rows.map((row) => row.map((cell) => this.csvCell(cell)).join(',')).join('\n')
            const file = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(file)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            a.style.visibility = 'hidden'
            document.body.appendChild(a)
            a.click()
            setTimeout(() => {
                document.body.removeChild(a)
                window.URL.revokeObjectURL(url)
            }, 0)
        },
        exportFileName(suffix) {
            const clean = (product) => product.nameversion.replace(/\s+/g, '_')
            return `compare_${clean(this.base)}_vs_${clean(this.target)}_${suffix}.csv`
        },
        saveComponentsToCSV() {
            const severities = this.compareData.severities
            const header = ['component', 'base_versions', 'target_versions', 'base_scan_date', 'target_scan_date']
            severities.forEach((severity) => header.push(`${severity.name}_base`, `${severity.name}_target`))

            const rows = [header]
            this.compareData.components.forEach((component) => {
                const row = [
                    component.name,
                    component.base_versions,
                    component.target_versions,
                    component.base_scan_date,
                    component.target_scan_date,
                ]
                severities.forEach((severity) => {
                    const data = component.severities.find((el) => el.id == severity.id)
                    row.push(data ? data.base_count : 0, data ? data.target_count : 0)
                })
                rows.push(row)
            })

            const total = ['Total', '', '', '', '']
            severities.forEach((severity) => {
                total.push(this.totalCount(severity.id, 'base_count'), this.totalCount(severity.id, 'target_count'))
            })
            rows.push(total)

            this.downloadCSV(rows, this.exportFileName('components'))
        },
        saveClosedCvesToCSV() {
            const rows = [['cve', 'component', 'mira_severity', 'package_name', 'package_version', 'fix_version', 'artifact']]
            this.compareData.closed_cves.forEach((cve) => {
                rows.push([cve.name, cve.component, cve.severity, cve.package_name, cve.package_version, cve.fix_version, cve.image])
            })
            this.downloadCSV(rows, this.exportFileName('closed_cves'))
        },
        resetComponentFilter() {
            // component names differ between releases, so drop the filter;
            // the component_filter watcher does the reload when it actually changed
            if (this.component_filter.length > 0) {
                this.component_filter = []
                return
            }
            this.reloadCompare()
        },
        reloadCompare() {
            const app = this

            if (app.base === null || app.target === null) {
                app.compareData = { component_names: [], severities: [], components: [], closed_cves: [] }
                return
            }

            this.showLoader();

            const params = new URLSearchParams()
            params.append('base', app.base.id)
            params.append('target', app.target.id)
            if (app.component_filter.length > 0) {
                params.append('components', app.component_filter)
            }

            app.$axios.get('/api/products/compare/', { params: params })
                .then((response) => { app.compareData = response.data })
                .catch(({ response }) => { console.error(response) })
                .finally(() => { this.hideLoader() });

            history.pushState(
                {},
                null,
                window.location.origin + "/#/products?" + params.toString()
            )
        },
    },
    watch: {
        base: function () { this.resetComponentFilter() },
        target: function () { this.resetComponentFilter() },
        component_filter: function () { this.reloadCompare() },
    },
    async created() {
        const app = this

        app.product_list = []
        await app.$axios.get('/api/product/')
            .then(response => { app.product_list = response.data.sort((a, b) => a.nameversion.localeCompare(b.nameversion)) })
            .catch(error => console.log(error))

        if (this.$route.query.base) {
            this.base = this.product_list.find((el) => String(el.id) === this.$route.query.base) || null
        }
        if (this.$route.query.target) {
            this.target = this.product_list.find((el) => String(el.id) === this.$route.query.target) || null
        }
        if (this.$route.query.components) {
            this.component_filter = this.$route.query.components.split(',').filter((el) => el !== '')
        }

        this.reloadCompare()
    },
}
</script>
